import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getDeal, claimDeal } from '../api';
import SEO from '../components/SEO';
import JsonLD from '../components/JsonLD';
import './DealDetailPage.css';

const EMO = {indian:'🍛',chinese:'🥡',italian:'🍕',mexican:'🌮',japanese:'🍱',thai:'🍜',vietnamese:'🍲',canadian:'🍁',american:'🍔',korean:'🥢',sushi:'🍣',pizza:'🍕',seafood:'🦞',bakery:'☕',breakfast:'🍳',vegetarian:'🥗',vegan:'🌿',other:'🍽'};
const PRICE = {1:'$',2:'$$',3:'$$$',4:'$$$$'};

const daysLeft = (d) => {
  const diff = Math.ceil((new Date(d) - new Date()) / 86400000);
  if (diff<=0) return 'Expired'; if (diff===1) return 'Last day!';
  if (diff<=3) return `${diff} days left`; return `${diff} days`;
};

export default function DealDetailPage() {
  const { id } = useParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { getDeal(id).then(setDeal).catch(()=>setDeal(null)).finally(()=>setLoading(false)); }, [id]);

  const handleClaim = async (e) => {
    e.preventDefault();
    if (!email) return setError('Please enter your email');
    setClaiming(true); setError('');
    try { const res = await claimDeal(id, email); setClaimed(res); }
    catch (err) { setError(err.message || 'Something went wrong'); }
    finally { setClaiming(false); }
  };

  if (loading) return (
    <div className="container" style={{padding:'60px 20px'}}>
      <div className="skeleton" style={{height:40,width:'60%',marginBottom:16}}/>
      <div className="skeleton" style={{height:300,borderRadius:16}}/>
    </div>
  );

  if (!deal) return (
    <div className="container text-center" style={{padding:'80px 20px'}}>
      <p style={{fontSize:48}}>😕</p>
      <p style={{marginTop:12,color:'var(--text-secondary)'}}>Deal not found or expired.</p>
      <Link to="/deals" className="btn btn-primary mt-3">Browse all deals</Link>
    </div>
  );

  return (
    <div className="deal-detail-page">
      <SEO
        title={`${deal.title} at ${deal.business_name} — ${deal.discount_percent}% off | Winnipeg`}
        description={`Save ${deal.discount_percent}% at ${deal.business_name} in Winnipeg. ${deal.description || deal.title} — was $${Number(deal.original_price).toFixed(2)}, now $${Number(deal.deal_price).toFixed(2)}. Claim instantly, no account needed.`}
        image={deal.image_url}
        url={`/deals/${deal.id}`}
        type="product"
      />
      <JsonLD data={{
        '@context': 'https://schema.org',
        '@type': 'Offer',
        'name': deal.title,
        'description': deal.description,
        'price': deal.deal_price,
        'priceCurrency': 'CAD',
        'priceValidUntil': deal.valid_until ? deal.valid_until.split('T')[0] : undefined,
        'url': `https://halfprice.ca/deals/${deal.id}`,
        'image': deal.image_url,
        'seller': {
          '@type': 'FoodEstablishment',
          'name': deal.business_name,
          'address': {
            '@type': 'PostalAddress',
            'streetAddress': deal.biz_address || deal.address,
            'addressLocality': 'Winnipeg',
            'addressRegion': 'MB',
            'addressCountry': 'CA'
          },
          'servesCuisine': deal.cuisine_type,
          'telephone': deal.biz_phone,
          'url': deal.website,
        }
      }} id="offer" />
      <JsonLD data={{
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://halfprice.ca' },
          { '@type': 'ListItem', 'position': 2, 'name': 'Deals', 'item': 'https://halfprice.ca/deals' },
          { '@type': 'ListItem', 'position': 3, 'name': deal.title, 'item': `https://halfprice.ca/deals/${deal.id}` },
        ]
      }} id="breadcrumb" />
      <div className="container">
        <Link to="/deals" className="back-link">← All deals</Link>
        <div className="deal-detail-grid">

          <div className="deal-detail-main fade-up">
            <div className="deal-detail-img">
              {deal.image_url ? <img src={deal.image_url} alt={deal.title}/> : <span style={{fontSize:72}}>{EMO[deal.cuisine_type]||'🍽'}</span>}
              {deal.is_new_restaurant && <span className="new-badge">New restaurant</span>}
            </div>
            <div className="deal-detail-body">
              <div className="deal-detail-meta-top">
                {deal.is_featured && <span className="badge badge-brand">Featured</span>}
                <span className="badge badge-muted">{deal.meal_type||'dine-in'}</span>
                {deal.cuisine_type && <span className="badge badge-muted">{EMO[deal.cuisine_type]} {deal.cuisine_type}</span>}
              </div>
              <p className="deal-detail-biz">{deal.business_name} · {deal.neighbourhood||deal.city_name}</p>
              <h1 className="deal-detail-title font-display">{deal.title}</h1>
              {deal.description && <p className="deal-detail-desc">{deal.description}</p>}
              {deal.what_you_get && <div className="what-you-get"><strong>What you get:</strong><p>{deal.what_you_get}</p></div>}
              <div className="deal-detail-pricing">
                <div><div className="dp-label">Deal price</div><div className="dp-price">${Number(deal.deal_price).toFixed(2)}</div></div>
                <div><div className="dp-label">Original</div><div className="dp-orig">${Number(deal.original_price).toFixed(2)}</div></div>
                <div><div className="dp-label">You save</div><div className="dp-save">{deal.discount_percent}%</div></div>
              </div>
              <div className="deal-meta-row">
                <div className="deal-meta-item">
                  <span className="meta-label">Expires</span>
                  <span className="meta-val">{new Date(deal.valid_until).toLocaleDateString('en-CA',{month:'long',day:'numeric',year:'numeric'})} · {daysLeft(deal.valid_until)}</span>
                </div>
                {deal.max_redemptions && <div className="deal-meta-item"><span className="meta-label">Claimed</span><span className="meta-val">{deal.current_redemptions}/{deal.max_redemptions}</span></div>}
                {deal.price_range && <div className="deal-meta-item"><span className="meta-label">Price range</span><span className="meta-val">{PRICE[deal.price_range]}</span></div>}
              </div>
              {deal.terms && <div className="deal-terms"><strong>Terms</strong><p>{deal.terms}</p></div>}
            </div>
          </div>

          <div className="claim-box">
            {claimed ? (
              <div className="claim-success">
                <div className="claim-success-icon">✓</div>
                <h2>Deal claimed!</h2>
                <p>Show this code at <strong>{deal.business_name}</strong></p>
                <div className="redemption-code">{claimed.redemption_code}</div>
                <div className="loyalty-section">
                  <p className="loyalty-label">Loyalty stamps at {deal.business_name}</p>
                  <div className="stamp-row" style={{justifyContent:'center',gap:10}}>
                    {[1,2,3].map(n=>(
                      <div key={n} className={`stamp ${n<=claimed.stamp_count?'filled':''}`} style={{width:28,height:28}}/>
                    ))}
                  </div>
                  <p className="loyalty-msg">{claimed.loyalty_message}</p>
                  {claimed.bonus_code && <div className="bonus-code"><span>Bonus code: </span><strong>{claimed.bonus_code}</strong></div>}
                </div>
                <p className="claim-note">Sent to <strong>{email}</strong></p>
                <div className="claim-instructions">
                  <strong>What to do:</strong>
                  <ol>
                    <li>Visit {deal.business_name} before {new Date(deal.valid_until).toLocaleDateString('en-CA')}</li>
                    <li>Show your code to the staff</li>
                    <li>Enjoy {deal.discount_percent}% off!</li>
                  </ol>
                </div>
                <Link to="/deals" className="btn btn-outline w-full mt-2" style={{justifyContent:'center'}}>Find more deals</Link>
              </div>
            ) : (
              <>
                <div className="claim-box-header">
                  <div className="claim-price">${Number(deal.deal_price).toFixed(2)}</div>
                  <div className="claim-was">was ${Number(deal.original_price).toFixed(2)}</div>
                  <div className="claim-save-badge">Save {deal.discount_percent}%</div>
                </div>
                <form onSubmit={handleClaim} className="claim-form">
                  <p className="claim-cta">Enter your email to claim this deal instantly</p>
                  <div className="form-group">
                    <input type="email" className="form-control" placeholder="your@email.com" value={email} onChange={e=>setEmail(e.target.value)} required/>
                  </div>
                  {error && <p className="form-error">{error}</p>}
                  <button type="submit" className="btn btn-primary w-full" disabled={claiming} style={{justifyContent:'center'}}>
                    {claiming ? 'Claiming…' : 'Claim this deal →'}
                  </button>
                  <p className="claim-fine">No account needed. No spam. Just your code.</p>
                  <div className="loyalty-preview">
                    <div className="stamp-row" style={{justifyContent:'center',gap:8}}>
                      {[1,2,3].map(n=><div key={n} className="stamp" style={{width:18,height:18}}/>)}
                    </div>
                    <p>Visit 3× to unlock a bonus deal</p>
                  </div>
                </form>
              </>
            )}
            <div className="biz-info-card">
              <h3>About {deal.business_name}</h3>
              {deal.biz_description && <p>{deal.biz_description}</p>}
              {deal.address && (
                <p className="biz-addr">
                  📍 <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(deal.address + ', Winnipeg, MB')}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color: 'var(--brand)' }}
                  >
                    {deal.address}
                  </a>
                </p>
              )}
              {deal.biz_phone && <p>📞 <a href={`tel:${deal.biz_phone}`} style={{ color: 'inherit' }}>{deal.biz_phone}</a></p>}
              {deal.instagram_handle && (
                <a href={`https://instagram.com/${deal.instagram_handle}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand)', fontSize: 13 }}>
                  @{deal.instagram_handle}
                </a>
              )}
              {deal.website && (
                <a href={deal.website} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand)', fontSize: 13, display: 'block', marginTop: 4 }}>
                  🌐 {deal.website.replace(/^https?:\/\//, '')}
                </a>
              )}
            </div>

            {/* Google Maps embed — free, no API key needed */}
            {deal.address && (
              <div className="deal-map">
                <iframe
                  title={`${deal.business_name} location map`}
                  width="100%"
                  height="200"
                  frameBorder="0"
                  style={{ borderRadius: 'var(--radius-md)', border: 0 }}
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(deal.address + ', Winnipeg, MB')}&output=embed&zoom=15`}
                  allowFullScreen
                  loading="lazy"
                />
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(deal.address + ', Winnipeg, MB')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="deal-map-directions"
                >
                  📍 Get directions →
                </a>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
